import { ExpectDriverDetail } from '../../../types';
import L, { LatLngExpression } from 'leaflet';
import React, { useState, useEffect, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import { Map, TileLayer, Marker } from 'react-leaflet';
import styled from 'styled-components';
import { useInterval } from '../Shared/hooks';

interface MapProps {
    trackingNumber: string;
    mapTileUrl: string;
    mapAttribution: string;
};

const MapWrapper = styled.div`
    width: 100%;
    height: 500px;
    @media (max-width: 480px) {
        height: 300px;
    }
`;

const MapComponent = ({ trackingNumber, mapTileUrl, mapAttribution }: MapProps) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [expectDetail, setExpectDetail] = useState<ExpectDriverDetail>();
    const mountedRef = useRef(false);

    const loadDriverDetails = () => {
        fetch(`Package/ExpectDetail?trackingNumber=${trackingNumber}`)
            .then(res => res.json())
            .then(
                (result) => {
                    if (mountedRef.current) {
                        setIsLoaded(true);
                        setExpectDetail(result);
                    }
                },
                (error) => {
                    console.log(error);
                }
            )
    };

    useEffect(() => {
        // Keep track of component state to stop updating unmounted component
        mountedRef.current = true;
        return () => { mountedRef.current = false };
    }, []);
    
    useEffect(loadDriverDetails, []); // load driver details
    useInterval(loadDriverDetails, 30000); // reload every 30 seconds

    if (isLoaded && expectDetail?.ShowLocation && expectDetail.AddressLatitude && expectDetail.AddressLongitude) {
        let center: number[] = [expectDetail.AddressLatitude, expectDetail.AddressLongitude]

        let centerBound: LatLngExpression = {lat: center[0], lng: center[1]}
        let bounds = L.latLngBounds([centerBound]);

        if (expectDetail.HasExpectDetail 
            && !expectDetail.IsAttempted
            && expectDetail.LastKnownDriverLatitude 
            && expectDetail.LastKnownDriverLongitude) {
            bounds.extend([expectDetail.LastKnownDriverLatitude, expectDetail.LastKnownDriverLongitude])
            bounds = bounds.pad(0.1)
        }

        return (
            <MapWrapper>
                <Map center={center} bounds={bounds} zoom={14} scrollWheelZoom={false} zoomControl={true}>
                    <TileLayer
                        attribution={mapAttribution}
                        url={mapTileUrl}
                    />

                    <Marker position={[expectDetail.AddressLatitude, expectDetail.AddressLongitude]} />

                    {expectDetail.HasExpectDetail && !expectDetail.IsAttempted && expectDetail.LastKnownDriverLatitude && expectDetail.LastKnownDriverLongitude &&
                        <DriverPin expectDetail={expectDetail} />
                    }
                </Map>
            </MapWrapper>
        )
    }

    return null;
};

const MarkerPinWrapper = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50% 50% 50% 0;
  background: ${p => p.color ? p.color : 'grey'};
  position: absolute;
  transform: rotate(-45deg);
  left: 50%;
  top: 50%;
  margin: -15px 0 0 -15px;

  &:after {
           content: '';
           width: 24px;
           height: 24px;
           margin: 3px 0 0 3px;
           background: ${p => p.highlight ? "#F2ED88" : "#FFFFFF"};
           position: absolute;
           border-radius: 50%;
       };
`

const FontIconWrapper = styled.i`
   position: absolute;
   width: 22px;
   font-size: 22px;
   color: ${p => p.color ? p.color : 'black'};
   left: 2px;
   right: 0px;
   margin: 10px auto;
   text-align: center;
`

const DriverPin = ({ expectDetail }) => {
    const icon = L.divIcon({
        className: 'custom-div-icon',
        html: ReactDOMServer.renderToString(
            <>
                <MarkerPinWrapper />
                <FontIconWrapper className='material-icons' color={'#4CC14C'}>local_shipping</FontIconWrapper>
            </>
        ),
        iconSize: [30, 42],
        iconAnchor: [15, 42]
    })

    return (
        <Marker
            icon={icon}
            title={expectDetail.DriverName}
            position={{ lat: expectDetail.LastKnownDriverLatitude, lng: expectDetail.LastKnownDriverLongitude }}
        />
    )
}

export default MapComponent;